import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import type { ChatMessageMetadata, ChatTurnRequest, ChatWidgetActionRequest } from "@shared/chatWidgets";

interface ChatMessage {
  id: number;
  conversationId: number;
  role: string;
  content: string | null;
  toolCalls: any;
  toolCallId: string | null;
  metadata: ChatMessageMetadata | null;
  createdAt: string;
}

interface ChatConversation {
  id: number;
  userId: number;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messages?: ChatMessage[];
}

interface ChatContextType {
  isOpen: boolean;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  currentConversationId: number | null;
  setCurrentConversationId: (id: number | null) => void;
  conversations: ChatConversation[];
  messages: ChatMessage[];
  isSending: boolean;
  isLoadingMessages: boolean;
  sendMessage: (content: string) => Promise<void>;
  sendWidgetAction: (widgetAction: ChatWidgetActionRequest, content?: string) => Promise<void>;
  createConversation: () => Promise<ChatConversation>;
  deleteConversation: (id: number) => Promise<void>;
  isConfigured: boolean | undefined;
  pendingUserMessage: ChatMessage | null;
  openChatWithMessage: (message: string) => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isConfigured, setIsConfigured] = useState<boolean | undefined>(undefined);
  const [pendingUserMessage, setPendingUserMessage] = useState<ChatMessage | null>(null);
  const [pendingInitialMessage, setPendingInitialMessage] = useState<string | null>(null);

  const toggleChat = useCallback(() => setIsOpen((prev) => !prev), []);
  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);

  const openChatWithMessage = useCallback((message: string) => {
    setCurrentConversationId(null);
    setPendingInitialMessage(message);
    setIsOpen(true);
  }, []);

  // Fetch conversation list
  const { data: conversations = [] } = useQuery<ChatConversation[]>({
    queryKey: ["/api/chat/conversations"],
    enabled: isAuthenticated && !!user?.verified && isOpen,
    staleTime: 30000,
  });

  // Fetch current conversation messages
  const { data: conversationData, isLoading: isLoadingMessages } = useQuery<ChatConversation>({
    queryKey: ["/api/chat/conversations/" + currentConversationId],
    enabled: !!currentConversationId && isAuthenticated && !!user?.verified,
    staleTime: 5000,
  });

  const serverMessages = conversationData?.messages || [];
  // Merge server messages with pending optimistic user message
  const messages = pendingUserMessage
    ? [...serverMessages, pendingUserMessage]
    : serverMessages;

  const createConversation = useCallback(async () => {
    const conv = await apiRequest("POST", "/api/chat/conversations", {});
    queryClient.invalidateQueries({ queryKey: ["/api/chat/conversations"] });
    setCurrentConversationId(conv.id);
    return conv;
  }, [queryClient]);

  const deleteConversation = useCallback(
    async (id: number) => {
      await apiRequest("DELETE", `/api/chat/conversations/${id}`);
      queryClient.invalidateQueries({ queryKey: ["/api/chat/conversations"] });
      if (currentConversationId === id) {
        setCurrentConversationId(null);
      }
    },
    [queryClient, currentConversationId]
  );

  const sendTurn = useCallback(
    async (payload: ChatTurnRequest) => {
      const transcriptContent = payload.content || payload.widgetAction?.summary || "";
      // Optimistic: show user message immediately
      const optimisticMsg: ChatMessage = {
        id: -Date.now(),
        conversationId: currentConversationId || 0,
        role: "user",
        content: transcriptContent || null,
        toolCalls: null,
        toolCallId: null,
        metadata: null,
        createdAt: new Date().toISOString(),
      };
      setPendingUserMessage(optimisticMsg);
      setIsSending(true);
      try {
        let convId = currentConversationId;
        if (!convId) {
          const conv = await createConversation();
          convId = conv.id;
        }

        await apiRequest("POST", `/api/chat/conversations/${convId}/messages`, payload);

        setIsConfigured(true);

        // Clear pending message since server data will include it
        setPendingUserMessage(null);

        // Refetch messages and conversations
        queryClient.invalidateQueries({ queryKey: ["/api/chat/conversations/" + convId] });
        queryClient.invalidateQueries({ queryKey: ["/api/chat/conversations"] });
      } catch (error: any) {
        if (error.message?.includes("503")) {
          setIsConfigured(false);
        }
        setPendingUserMessage(null);
        throw error;
      } finally {
        setIsSending(false);
      }
    },
    [currentConversationId, createConversation, queryClient]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      return sendTurn({ content });
    },
    [sendTurn]
  );

  const sendWidgetAction = useCallback(
    async (widgetAction: ChatWidgetActionRequest, content?: string) => {
      return sendTurn({ content, widgetAction });
    },
    [sendTurn]
  );

  // Auto-send pending initial message once chat is open
  useEffect(() => {
    if (isOpen && pendingInitialMessage && !isSending) {
      const msg = pendingInitialMessage;
      setPendingInitialMessage(null);
      sendMessage(msg).catch(console.error);
    }
  }, [isOpen, pendingInitialMessage, isSending, sendMessage]);

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        toggleChat,
        openChat,
        closeChat,
        currentConversationId,
        setCurrentConversationId,
        conversations,
        messages,
        isSending,
        isLoadingMessages,
        sendMessage,
        sendWidgetAction,
        createConversation,
        deleteConversation,
        isConfigured,
        pendingUserMessage,
        openChatWithMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
